// Every photo the editor shows is printed — or no PDF exists.
//
// ⚠️ 2026-09-26, #1215 (Mariam, 56 pages): the editor showed every page full;
// the PDF and the 3D preview had pages 2, 5 and 34 empty. The four photos were
// there, readable, at full resolution. pdfGenerator's per-slot `catch` painted
// the slot cream and carried on — « the customer just sees blank cream » — so
// the PDF "succeeded" and nothing anywhere knew. What threw was the 20 Sept
// orientation lock (assertSameOrientation), swallowed like every other failure.
//
// The rule now: a placed photo that cannot be drawn STOPS the render with the
// page and the photo, and every page is counted (drawn vs shown) before the PDF
// is allowed to exist. The message starts with « print-integrity » because the
// worker reads that as a data problem: alert a person at once, never retry
// (worker/src/lib/renderDeadAlert.ts isRetriableFailure).
//
// Pure. Guard: printCompleteness.test.ts, renderParity.test.ts (section E).

export class PrintIntegrityError extends Error {
  readonly albumPage: number;
  readonly photoIds: string[];
  constructor(message: string, albumPage: number, photoIds: string[]) {
    super(message);
    this.name = 'PrintIntegrityError';
    this.albumPage = albumPage;
    this.photoIds = photoIds;
  }
}

const short = (id: string | null | undefined) => String(id ?? '?').slice(0, 8);

/** One placed photo could not be drawn. `albumPage` is 1-based, as the editor shows it. */
export function unprintablePhoto(ctx: { albumPage: number; photoId: string | null | undefined; reason: string }): PrintIntegrityError {
  const reason = String(ctx.reason ?? '').replace(/\s+/g, ' ').slice(0, 160);
  return new PrintIntegrityError(
    `print-integrity: album page ${ctx.albumPage} — photo ${short(ctx.photoId)} could not be printed (${reason}). `
      + 'No PDF was made: fix the photo, then regenerate.',
    ctx.albumPage,
    ctx.photoId ? [ctx.photoId] : [],
  );
}

/** A cover image or cover photo could not be drawn — a blank cover is a hole too. */
export function unprintableCover(ctx: { part: string; photoId?: string | null; reason: string }): PrintIntegrityError {
  const reason = String(ctx.reason ?? '').replace(/\s+/g, ' ').slice(0, 160);
  const which = ctx.photoId ? ` photo ${short(ctx.photoId)}` : '';
  return new PrintIntegrityError(
    `print-integrity: ${ctx.part}${which} could not be printed (${reason}). No PDF was made: fix it, then regenerate.`,
    0,
    ctx.photoId ? [ctx.photoId] : [],
  );
}

/** The photos the editor shows in this page's slots: one per slot, slot order, empties skipped. */
export function expectedPhotosOnPage(photos: ReadonlyArray<string | null | undefined> | undefined, slotCount: number): string[] {
  const out: string[] = [];
  for (let si = 0; si < slotCount; si++) {
    const pid = photos?.[si];
    if (typeof pid === 'string' && pid) out.push(pid);
  }
  return out;
}

/** Photos per page, editor vs PDF: every photo the editor shows must have been drawn. */
export function assertPageComplete(albumPage: number, expected: ReadonlyArray<string>, drawn: ReadonlySet<string>): void {
  const missing = expected.filter((pid) => !drawn.has(pid));
  if (!missing.length) return;
  throw new PrintIntegrityError(
    `print-integrity: album page ${albumPage} — ${missing.length} of ${expected.length} photo(s) shown in the editor `
      + `were not drawn (${missing.map(short).join(', ')}). No PDF was made.`,
    albumPage,
    missing,
  );
}
